import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";
import AppStoreButtons from "@/components/feature/AppStoreButtons";
import { useModal } from "@/hooks/useModal";

const features = [
  {
    icon: "ri-user-heart-line",
    title: "Patient Management",
    desc: "Complete patient registration, scheduling, visit tracking, and medical history in one unified profile.",
  },
  {
    icon: "ri-file-list-3-line",
    title: "Electronic Medical Records",
    desc: "Structured EMR with clinical notes, prescriptions, allergies, vitals, and attachments.",
  },
  {
    icon: "ri-bank-card-line",
    title: "Billing & Insurance",
    desc: "Automated invoicing, insurance claim workflows, and real-time payment tracking.",
  },
  {
    icon: "ri-test-tube-line",
    title: "Lab Integration",
    desc: "Seamless lab order management, result tracking, and integration with diagnostic partners.",
  },
  {
    icon: "ri-capsule-line",
    title: "Pharmacy",
    desc: "Inventory management, dispensing, and drug interaction checks for in-hospital pharmacies.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Analytics & Reporting",
    desc: "Operational dashboards, patient flow insights, and regulatory reporting at your fingertips.",
  },
];

const howItWorks = [
  {
    step: "01",
    title: "Setup Hospital",
    desc: "We configure departments, roles, workflows, and integrations tailored to your facility.",
  },
  {
    step: "02",
    title: "Train Staff",
    desc: "Hands-on training sessions for doctors, nurses, admin, and lab teams.",
  },
  {
    step: "03",
    title: "Go Live",
    desc: "Phased rollout with on-site support and real-time troubleshooting.",
  },
];

const screenshots = [
  {
    src: "https://storage.readdy-site.link/project_files/47b2e0f1-f782-46fd-9795-faa524ec7a75/a559afac-450d-4ce3-aebd-85f487f65729_Presentation-cover-grid-4.png?v=fbda99f309d65fe9379063991bd0accd",
    label: "Dashboard Overview",
    desc: "Real-time hospital operations at a glance.",
  },
  {
    src: "https://storage.readdy-site.link/project_files/47b2e0f1-f782-46fd-9795-faa524ec7a75/a69a665c-fe43-49c8-a431-6a28d13c2703_Presentation-cover-grid-10.png?v=1ab43982a3b3681edba78c43093fc587",
    label: "Patient Records",
    desc: "Complete EMR with visit history and vitals.",
  },
  {
    src: "https://storage.readdy-site.link/project_files/47b2e0f1-f782-46fd-9795-faa524ec7a75/675ea43e-9bc6-4704-8ad5-ea287a049709_Presentation-cover-grid-7.png?v=f986085688eb5fcea2aaf659d759e842",
    label: "Lab & Pharmacy",
    desc: "Integrated diagnostics and inventory management.",
  },
];

export default function Ahis() {
  const { openDemo } = useModal();

  return (
    <PageShell>
      <section className="w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold mb-6">
                <i className="ri-shield-check-line" />
                Built by Presuni - Trusted IT &amp; Cybersecurity Partner since 2006
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 max-w-3xl mx-auto">
                The Operating System for Modern Hospitals
              </h1>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                Unified platform for patients, records, billing, labs, pharmacy, and operations.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <button
                onClick={openDemo}
                className="px-7 py-3.5 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/25 whitespace-nowrap cursor-pointer"
              >
                Book Demo
              </button>
              <button
                onClick={openDemo}
                className="px-7 py-3.5 rounded-full bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border text-gray-700 dark:text-gray-300 text-sm font-semibold hover:border-brand-300 dark:hover:border-brand-500/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                Start Trial
              </button>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={1}>
            <AppStoreButtons className="justify-center mb-12" />
          </ScrollReveal>

          <ScrollReveal>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto border border-gray-200 dark:border-dark-border">
              <img
                src="https://storage.readdy-site.link/project_files/47b2e0f1-f782-46fd-9795-faa524ec7a75/a559afac-450d-4ce3-aebd-85f487f65729_Presentation-cover-grid-4.png?v=fbda99f309d65fe9379063991bd0accd"
                alt="AHIS Dashboard Overview"
                className="w-full h-[280px] md:h-[480px] object-cover rounded-2xl"
                loading="eager"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Screenshots
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
                See AHIS in Action
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                Real interfaces from the AHIS platform - built for the way hospitals actually work.
              </p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {screenshots.map((shot, i) => (
              <ScrollReveal key={shot.label} delay={i + 1}>
                <div className="group">
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-dark-border shadow-lg mb-4">
                    <img
                      src={shot.src}
                      alt={shot.label}
                      className="w-full h-[220px] md:h-[280px] object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    {shot.label}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {shot.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center max-w-5xl mx-auto">
            <ScrollReveal>
              <span className="inline-block px-3 py-1 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-semibold uppercase tracking-wider mb-4">
                The Problem
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Hospitals Are Still Running on Paper
              </h2>
              <ul className="space-y-3">
                {[
                  "Paper records are slow, error-prone, and hard to retrieve",
                  "Fragmented systems mean data lives in silos",
                  "Inefficient workflows cost time, money, and lives",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                    <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
            <ScrollReveal>
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                The Solution
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                One System. Every Department.
              </h2>
              <ul className="space-y-3">
                {[
                  "Unified patient data across all departments",
                  "Automated billing, scheduling, and reporting",
                  "Real-time access from any device, anywhere",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                    <i className="ri-check-line text-brand-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Features
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
                Everything Your Hospital Needs
              </h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <ScrollReveal key={f.title} delay={(i % 3) + 1}>
                <div className="group p-6 md:p-7 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border hover:border-brand-200 dark:hover:border-brand-500/30 transition-all duration-300 h-full">
                  <div className="w-11 h-11 rounded-lg bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${f.icon} text-lg text-brand-500`} />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">
                    {f.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                How It Works
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
                From Setup to Go-Live
              </h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            {howItWorks.map((step, i) => (
              <ScrollReveal key={step.step} delay={i + 1}>
                <div className="text-center p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border">
                  <div className="text-4xl md:text-5xl font-extrabold text-brand-500/20 mb-3">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
            <ScrollReveal>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Security First
              </h2>
              <ul className="space-y-3">
                {[
                  "End-to-end encryption for all patient data",
                  "Role-based access control with audit trails",
                  "Comprehensive audit logs for compliance",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                    <i className="ri-shield-check-line text-brand-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
            <ScrollReveal>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Integrations
              </h2>
              <ul className="space-y-3">
                {[
                  "Lab systems and diagnostic equipment",
                  "Insurance providers and claims APIs",
                  "Payment gateways and accounting tools",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                    <i className="ri-plug-line text-brand-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-brand-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Digitize Your Hospital Today
          </h2>
          <p className="text-sm md:text-base text-brand-100 max-w-lg mx-auto mb-8">
            Join hospitals already running on AHIS. Book a demo and see it in action.
          </p>
          <button
            onClick={openDemo}
            className="px-8 py-3.5 rounded-full bg-white text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            Book Demo
          </button>
        </div>
      </section>
    </PageShell>
  );
}
